import { useMemo } from "react"
import { useAppSelector } from "../../Common/_hooks/useAppSelector";
import { DateTime } from "luxon";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import SeoLinks, { type AppLocale, buildAbsoluteSeoUrl, getCanonicalLocale, getI18nextLocale, joinSeoPath } from "../../Common/SEO/SeoLinks";
import StructuredData, {
  buildBreadcrumbListSchema,
  getSeoHomeName,
  getSeoProductsName,
  type SeoListItem,
} from "../../Common/SEO/StructuredData";
import { getLocalizedViewCategorySlug, getProductCategoryPath } from './productBreadcrumbData';

interface Props {
  viewProductId: number
  title: string
  description: string
}

const getProductImageUrl = (slug: string): string => {
  // Product images are bundled by slug throughout the product pages.
  // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-unsafe-return
  return require('@resources/img/all-products/products/' + slug + '.jpg') as string;
}

export default function ProductSEO(props: Props) {
  const [, routesI18n] = useTranslation('routes')
  const viewProductData = useAppSelector(state => state.get('appData').get('viewProducts').get(String(props.viewProductId)));
  const viewCategories = useAppSelector(state => state.get('appData').get('viewCategories'));
  const currentLocale = getCanonicalLocale(routesI18n.resolvedLanguage ?? routesI18n.language);
  const productSeoPaths = useMemo(() => {
    const buildProductPath = (locale: AppLocale) => {
      const productBasePath = routesI18n.getFixedT(getI18nextLocale(locale), 'routes')('/products');
      const urlSlug = viewProductData?.get('url_slugs').get(locale) ?? viewProductData?.get('url_slug') ?? '';

      return joinSeoPath(productBasePath, urlSlug);
    }

    return {
      en_CA: buildProductPath('en_CA'),
      fr_CA: buildProductPath('fr_CA'),
    }
  }, [routesI18n, viewProductData])
  const canonicalUrl = buildAbsoluteSeoUrl(productSeoPaths[currentLocale]);

  const productSchema = useMemo(() => {
    if (!viewProductData) return null

    return {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: viewProductData.get('name'),
      image: getProductImageUrl(viewProductData.get('slug')),
      description: viewProductData.get('description'),
      url: canonicalUrl,
      sku: 'product-' + viewProductData.get('id'),
      brand: {
        '@type': 'Brand',
        name: 'Art of Where'
      },
      offers: {
        '@type': 'Offer',
        price: viewProductData.get('price').toDinero().toRoundedUnit(2).toFixed(2),
        priceCurrency: viewProductData.get('price').get('currency'),
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        url: canonicalUrl,
        //Price is set to be valid until middle of next year
        priceValidUntil: DateTime.now().plus({ year: 1 }).setZone('utc').set({
          month: 6,
          day: 1,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        }).toISODate(),
      }
    }
  }, [canonicalUrl, viewProductData])

  const breadcrumbSchema = useMemo(() => {
    if (!viewProductData) return null;

    const fixedRoutes = routesI18n.getFixedT(getI18nextLocale(currentLocale), 'routes');
    const productBasePath = fixedRoutes('/products');
    const breadcrumbItems: SeoListItem[] = [
      {
        name: getSeoHomeName(currentLocale),
        url: buildAbsoluteSeoUrl(fixedRoutes('/')),
      },
      {
        name: getSeoProductsName(currentLocale),
        url: buildAbsoluteSeoUrl(productBasePath),
      },
    ];

    const categoryPath: string[] = [];

    getProductCategoryPath(viewProductData, viewCategories).forEach(category => {
      categoryPath.push(getLocalizedViewCategorySlug(category, currentLocale));
      breadcrumbItems.push({
        name: category.get('name'),
        url: buildAbsoluteSeoUrl(joinSeoPath(productBasePath, ...categoryPath)),
      });
    });

    breadcrumbItems.push({
      name: viewProductData.get('name'),
      url: canonicalUrl,
    });

    return buildBreadcrumbListSchema(breadcrumbItems);
  }, [canonicalUrl, currentLocale, routesI18n, viewCategories, viewProductData])

  if (productSchema === null || !viewProductData) return null

  return <>
    <SeoLinks
      canonicalPath={productSeoPaths[currentLocale]}
      alternatePaths={productSeoPaths}
    />

    <Helmet>
      <title>{props.title}</title>
      <meta name="description" content={props.description} />

      <meta property="og:title" content={props.title} />
      <meta property="og:description" content={props.description} />
      <meta property="og:image" content={getProductImageUrl(viewProductData.get('slug'))} />
      <meta property="og:type" content="product" />
      <meta property="product:price.amount" content={viewProductData.get('price').toDinero().toRoundedUnit(2).toFixed(2)} />
      <meta property="product:price.currency" content={viewProductData.get('price').get('currency')} />

      <meta name="twitter:card" content="summary"/>
      <meta name="twitter:title" content={props.title} />
      <meta name="twitter:description" content={props.description} />
      <meta name="twitter:image" content={getProductImageUrl(viewProductData.get('slug'))} />
    </Helmet>

    <StructuredData data={productSchema} />
    <StructuredData data={breadcrumbSchema} />
  </>
}
